(() => {
  const DEFAULT_PACK_SIZE = 3;
  const PACK_TIERS = {
    3: 10,
    5: 15,
    10: 20
  };

  const moneyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD'
  });

  const formatMoney = (cents) => moneyFormatter.format((Number(cents) || 0) / 100);

  const unique = (values) => [...new Set(values.filter(Boolean))];

  const getVariantImage = (variant) => {
    if (!variant) return '';
    const image = variant.featured_image || variant.featured_media || {};
    return image.src || image.preview_image?.src || '';
  };

  const shouldMoveBelowAddToCart = () => {
    const position = new URLSearchParams(window.location.search).get('pack_builder_position');
    return ['below_atc', 'under_atc', 'after_add_to_cart'].includes(position);
  };

  const moveBelowAddToCart = (root) => {
    if (!shouldMoveBelowAddToCart()) return;

    const sectionId = root.dataset.sectionId;
    const submitButton = document.getElementById(`ProductSubmitButton-${sectionId}`);
    const productForm = submitButton?.closest('product-form');
    const quantityForm = submitButton?.closest('[id^="Quantity-Form-"]');
    const insertionTarget = quantityForm || productForm;

    if (!insertionTarget || insertionTarget.nextElementSibling === root) return;
    root.classList.add('chill-pack-builder--below-atc');
    insertionTarget.insertAdjacentElement('afterend', root);
  };

  class ChillPackBuilder {
    constructor(root) {
      moveBelowAddToCart(root);

      this.root = root;
      this.sectionId = root.dataset.sectionId;
      this.tierOptionsEl = root.querySelector('[data-pack-tier-options]');
      this.headingEl = root.querySelector('[data-pack-heading]');
      this.subtitleEl = root.querySelector('[data-pack-subtitle]');
      this.helperEl = root.querySelector('[data-pack-helper]');
      this.addLabelEl = root.querySelector('[data-pack-add-label]');
      this.summaryLabel = root.querySelector('[data-pack-summary-label]');
      this.sizeOptionsEl = root.querySelector('[data-pack-size-options]');
      this.colorOptionsEl = root.querySelector('[data-pack-color-options]');
      this.slotsEl = root.querySelector('[data-pack-slots]');
      this.countEl = root.querySelector('[data-pack-count]');
      this.submitButton = root.querySelector('[data-pack-submit]');
      this.submitText = root.querySelector('[data-pack-submit-text]');
      this.spinner = root.querySelector('[data-pack-spinner]');
      this.errorEl = root.querySelector('[data-pack-error]');
      this.summaryNote = root.querySelector('[data-pack-summary-note]');
      this.options = this.readJson('[data-pack-builder-options]', []);
      this.variants = this.readJson('[data-pack-builder-variants]', []);
      this.selectedVariants = [];
      this.selectedSize = '';
      this.lastAddedVariantId = null;
      this.lastAddedTimer = null;
      this.hasSelectedPackTier = false;
      this.packSize = DEFAULT_PACK_SIZE;
      this.packDiscount = PACK_TIERS[DEFAULT_PACK_SIZE];

      this.optionIndexes = this.detectOptionIndexes();
      this.sizes = unique(this.variants.map((variant) => this.getOptionValue(variant, this.optionIndexes.size)));
      this.colors = unique(this.variants.map((variant) => this.getOptionValue(variant, this.optionIndexes.color)));

      if (!this.variants.length || !this.sizes.length || !this.colors.length) {
        this.root.hidden = true;
        return;
      }

      this.selectedSize = this.findInitialSize();
      this.bindEvents();
      this.render();
    }

    readJson(selector, fallback) {
      const script = this.root.querySelector(selector);
      if (!script) return fallback;
      try {
        return JSON.parse(script.textContent || '');
      } catch (error) {
        console.error('Chill Boys pack builder JSON parse error', error);
        return fallback;
      }
    }

    detectOptionIndexes() {
      const normalized = this.options.map((option) => String(option || '').toLowerCase());
      const sizeIndex = normalized.findIndex((option) => option.includes('size'));
      const colorIndex = normalized.findIndex((option) => option.includes('color') || option.includes('print') || option.includes('style'));

      return {
        size: sizeIndex >= 0 ? sizeIndex : Math.min(this.options.length - 1, 1),
        color: colorIndex >= 0 ? colorIndex : 0
      };
    }

    findInitialSize() {
      const firstAvailable = this.variants.find((variant) => variant.available);
      return this.getOptionValue(firstAvailable, this.optionIndexes.size) || this.sizes[0];
    }

    getOptionValue(variant, index) {
      if (!variant || index < 0) return '';
      if (Array.isArray(variant.options)) return variant.options[index] || '';
      return variant[`option${index + 1}`] || '';
    }

    findVariantForColor(color) {
      return this.variants.find((variant) => {
        return this.getOptionValue(variant, this.optionIndexes.size) === this.selectedSize &&
          this.getOptionValue(variant, this.optionIndexes.color) === color;
      });
    }

    bindEvents() {
      this.tierOptionsEl.addEventListener('click', (event) => {
        const button = event.target.closest('[data-pack-tier]');
        if (!button) return;
        this.packSize = Number(button.dataset.packTier) || DEFAULT_PACK_SIZE;
        this.packDiscount = Number(button.dataset.packDiscount) || PACK_TIERS[this.packSize] || 0;
        this.hasSelectedPackTier = true;
        this.selectedVariants = this.selectedVariants.slice(0, this.packSize);
        this.lastAddedVariantId = null;
        this.hideError();
        this.render();
      });

      this.sizeOptionsEl.addEventListener('click', (event) => {
        const button = event.target.closest('[data-pack-size]');
        if (!button) return;
        this.selectedSize = button.dataset.packSize;
        this.lastAddedVariantId = null;
        this.hideError();
        this.render();
      });

      this.colorOptionsEl.addEventListener('click', (event) => {
        const button = event.target.closest('[data-pack-add]');
        if (!button || button.disabled) return;
        const variantId = Number(button.dataset.variantId);
        const variant = this.variants.find((item) => Number(item.id) === variantId);
        if (!variant || !variant.available || this.selectedVariants.length >= this.packSize) return;
        this.selectedVariants.push(variant);
        this.flashAddedVariant(variant.id);
        this.hideError();
        this.render();
      });

      this.slotsEl.addEventListener('click', (event) => {
        const button = event.target.closest('[data-pack-remove]');
        if (!button) return;
        this.selectedVariants.splice(Number(button.dataset.packRemove), 1);
        this.lastAddedVariantId = null;
        this.hideError();
        this.render();
      });

      this.submitButton.addEventListener('click', () => this.addPackToCart());
    }

    flashAddedVariant(variantId) {
      this.lastAddedVariantId = Number(variantId);
      window.clearTimeout(this.lastAddedTimer);
      this.lastAddedTimer = window.setTimeout(() => {
        this.lastAddedVariantId = null;
        this.renderColors();
      }, 1100);
    }

    render() {
      this.renderTierOptions();
      this.renderSizes();
      this.renderColors();
      this.renderSlots();
      this.renderSummary();
    }

    renderTierOptions() {
      this.tierOptionsEl.querySelectorAll('[data-pack-tier]').forEach((button) => {
        button.setAttribute('aria-checked', String(Number(button.dataset.packTier) === this.packSize));
      });
      this.headingEl.textContent = this.hasSelectedPackTier ? `Build Your Own ${this.packSize}-Pack` : 'Build Your Own Pack';
      this.helperEl.textContent = `Select ${this.packSize} pairs to unlock ${this.packDiscount}% off.`;
      this.addLabelEl.textContent = `2. Add ${this.packSize} pairs`;
      this.summaryLabel.textContent = `Custom ${this.packSize}-pack`;
    }

    renderSizes() {
      this.sizeOptionsEl.innerHTML = this.sizes.map((size) => {
        const hasAvailable = this.colors.some((color) => {
          const variant = this.variants.find((item) => {
            return this.getOptionValue(item, this.optionIndexes.size) === size &&
              this.getOptionValue(item, this.optionIndexes.color) === color;
          });
          return variant && variant.available;
        });

        return `<button
          class="chill-pack-builder__size"
          type="button"
          role="radio"
          aria-checked="${size === this.selectedSize}"
          data-pack-size="${this.escapeAttr(size)}"
          ${hasAvailable ? '' : 'disabled'}
        >${this.escapeHtml(size)}</button>`;
      }).join('');
    }

    renderColors() {
      this.colorOptionsEl.innerHTML = this.colors.map((color) => {
        const variant = this.findVariantForColor(color);
        const image = getVariantImage(variant);
        const unavailable = !variant || !variant.available;
        const full = this.selectedVariants.length >= this.packSize;
        const disabled = unavailable || full;
        const price = variant ? formatMoney(variant.price) : '';

        const selectedCount = variant ? this.selectedVariants.filter((item) => Number(item.id) === Number(variant.id)).length : 0;
        const justAdded = variant && Number(variant.id) === this.lastAddedVariantId;
        const buttonLabel = justAdded ? 'Added ✓' : selectedCount ? `Add another (${selectedCount})` : 'Add';

        return `<div class="chill-pack-builder__option${unavailable ? ' is-unavailable' : ''}${selectedCount ? ' is-selected' : ''}${justAdded ? ' is-just-added' : ''}">
          <div class="chill-pack-builder__option-image">${image ? `<img src="${this.escapeAttr(image)}" alt="${this.escapeAttr(color)}">` : ''}</div>
          <div>
            <div class="chill-pack-builder__option-name">${this.escapeHtml(color)}</div>
            <div class="chill-pack-builder__option-meta">${unavailable ? 'Unavailable in ' + this.escapeHtml(this.selectedSize) : selectedCount ? `${selectedCount} selected · ${price}` : price}</div>
          </div>
          <button class="chill-pack-builder__add" type="button" data-pack-add data-variant-id="${variant ? variant.id : ''}" ${disabled ? 'disabled' : ''}>${this.escapeHtml(buttonLabel)}</button>
        </div>`;
      }).join('');
    }

    renderSlots() {
      const slots = [];
      for (let index = 0; index < this.packSize; index += 1) {
        const variant = this.selectedVariants[index];
        if (!variant) {
          slots.push(`<div class="chill-pack-builder__slot is-empty">Pair ${index + 1}</div>`);
          continue;
        }

        const color = this.getOptionValue(variant, this.optionIndexes.color);
        const size = this.getOptionValue(variant, this.optionIndexes.size);
        const image = getVariantImage(variant);
        slots.push(`<div class="chill-pack-builder__slot">
          <button class="chill-pack-builder__remove" type="button" aria-label="Remove ${this.escapeAttr(color)}" data-pack-remove="${index}">×</button>
          <div class="chill-pack-builder__slot-image">${image ? `<img src="${this.escapeAttr(image)}" alt="${this.escapeAttr(color)}">` : ''}</div>
          <div class="chill-pack-builder__slot-title">${this.escapeHtml(color)}</div>
          <div class="chill-pack-builder__slot-meta">Size ${this.escapeHtml(size)}</div>
        </div>`);
      }

      this.slotsEl.innerHTML = slots.join('');
    }

    renderSummary() {
      const count = this.selectedVariants.length;
      this.countEl.textContent = `${count}/${this.packSize}`;
      this.submitButton.disabled = count !== this.packSize;
      this.submitText.textContent = count === this.packSize ? `Add ${this.packSize} to cart` : `Add ${this.packSize - count} more`;

      if (count === this.packSize) {
        const total = this.selectedVariants.reduce((sum, variant) => sum + Number(variant.price || 0), 0);
        const discountedTotal = Math.round(total * (100 - this.packDiscount) / 100);
        this.summaryNote.innerHTML = `<span class="chill-pack-builder__price-original">${formatMoney(total)}</span> <span class="chill-pack-builder__price-sale">${formatMoney(discountedTotal)}</span> <span class="chill-pack-builder__savings">${this.packDiscount}% off</span>`;
      } else {
        this.summaryNote.textContent = `Select ${this.packSize} pairs to unlock ${this.packDiscount}% off.`;
      }
    }

    async addPackToCart() {
      if (this.selectedVariants.length !== this.packSize || this.submitButton.disabled) return;

      this.setLoading(true);
      this.hideError();

      try {
        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const payload = {
          items: this.selectedVariants.map((variant) => ({ id: Number(variant.id), quantity: 1 })),
          sections_url: window.location.pathname
        };

        if (cart && typeof cart.getSectionsToRender === 'function') {
          payload.sections = cart.getSectionsToRender().map((section) => section.id);
          if (typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);
        }

        const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify(payload)
        });

        const parsed = await response.json();
        if (!response.ok || parsed.status) {
          throw new Error(parsed.description || parsed.message || 'Could not add this pack to cart. Please try again.');
        }

        if (cart && typeof cart.renderContents === 'function' && parsed.sections) {
          if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
          cart.renderContents(parsed);
        } else {
          window.location.href = window.routes?.cart_url || '/cart';
        }

        this.selectedVariants = [];
        this.lastAddedVariantId = null;
        this.render();
      } catch (error) {
        this.showError(error.message || 'Could not add this pack to cart. Please try again.');
      } finally {
        this.setLoading(false);
      }
    }

    setLoading(isLoading) {
      this.submitButton.toggleAttribute('aria-disabled', isLoading);
      this.submitButton.disabled = isLoading || this.selectedVariants.length !== this.packSize;
      this.submitButton.classList.toggle('loading', isLoading);
      this.spinner?.classList.toggle('hidden', !isLoading);
    }

    showError(message) {
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
    }

    hideError() {
      this.errorEl.textContent = '';
      this.errorEl.hidden = true;
    }

    escapeHtml(value) {
      return String(value || '').replace(/[&<>'"]/g, (char) => ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        "'": '&#39;',
        '"': '&quot;'
      })[char]);
    }

    escapeAttr(value) {
      return this.escapeHtml(value);
    }
  }

  const initPackBuilders = () => {
    document.querySelectorAll('[data-chill-pack-builder]:not([data-pack-builder-ready])').forEach((root) => {
      root.dataset.packBuilderReady = 'true';
      new ChillPackBuilder(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPackBuilders);
  } else {
    initPackBuilders();
  }
})();
